// src/components/Chat/MessageBubble.jsx
import {
    Paper,
    Typography,
    Box,
    Chip,
    Avatar,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

/* ────────────────────────────────────────
   익명 아바타 색상 팔레트 (50색)
   ──────────────────────────────────────── */
const AvatarColors = [
    "#FF6B6B", "#FF8E72", "#FFA36C", "#FFBE76", "#FFD93D",
    "#FFE66D", "#E9FF70", "#C7F464", "#9AE66E", "#77DD77",
    "#4BCF92", "#2ECC71", "#1ABC9C", "#4ECDC4", "#45B7AF",
    "#6CCFF6", "#4D9DE0", "#5AA9E6", "#5887FF", "#4F6DFF",

    "#6A7FDB", "#8A74F9", "#9D8DF1", "#B28DFF", "#C77DFF",
    "#E484FF", "#FF99CC", "#FF7EB9", "#F46A8C", "#E66A6A",

    "#C75C5C", "#D9806F", "#F0A868", "#F4C361", "#F6E58D",
    "#C1E1C5", "#A3D9A5", "#90CCF4", "#73B9FF", "#6F8DEB",

    "#A29BFE", "#B9A0FF", "#FFB3BA", "#FFDFBA", "#FFFFBA",
    "#BAFFC9", "#BAE1FF", "#D5B8FF"
];

/* 이름 기반 고정 컬러 선택 */
function getColorFromName(name) {
    const safeName = name || '';
    let hash = 0;
    for (let i = 0; i < safeName.length; i++) {
        hash = safeName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash % AvatarColors.length);
    return AvatarColors[index];
}

/* HEX → RGB 변환 */
function hexToRgb(hex) {
    if (!hex) return null;
    let cleaned = hex.replace('#', '');
    if (cleaned.length === 3) {
        cleaned = cleaned.split('').map(ch => ch + ch).join('');
    }
    if (cleaned.length !== 6) return null;
    const num = parseInt(cleaned, 16);
    return {
        r: (num >> 16) & 255,
        g: (num >> 8) & 255,
        b: num & 255,
    };
}

/* 배경색에 따라 텍스트색 자동 결정 */
function getContrastTextColor(bgColor) {
    const rgb = hexToRgb(bgColor);
    if (!rgb) return '#fff';
    // 가독성용 간단 밝기 계산
    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    // 값이 클수록 밝은 색 → 어두운 글자, 아니면 흰 글자
    return brightness > 150 ? '#333' : '#fff';
}

/* ────────────────────────────────────────
   헤더(닉네임 · 칭호 · 이모티콘)
   ──────────────────────────────────────── */
const MessageHeader = ({
    isMine,
    name,
    mainTitle,
    subTitle,
    emoticonUrl,
    emoticonText,
    isAnonymousChat,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: isMine ? 'flex-end' : 'flex-start',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: isMobile ? 0.3 : 0.5,
                mb: isMobile ? 0.3 : 0.5,
                fontSize: isMobile ? '0.75rem' : undefined,
            }}
        >
            {!isAnonymousChat && subTitle && (
                <Chip
                    label={subTitle}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: isMobile ? '0.7rem' : undefined }}
                />
            )}
            {!isAnonymousChat && mainTitle && (
                <Chip
                    label={mainTitle}
                    size="small"
                    color="primary"
                    sx={{ fontSize: isMobile ? '0.7rem' : undefined }}
                />
            )}
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: isMobile ? '0.75rem' : undefined,
                }}
            >
                {name}
            </Typography>
            {!isAnonymousChat && emoticonUrl && (
                <Avatar
                    src={emoticonUrl}
                    sx={{ width: isMobile ? 16 : 18, height: isMobile ? 16 : 18 }}
                />
            )}
            {!isAnonymousChat && emoticonText && (
                <Typography
                    variant="caption"
                    sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
                >
                    {emoticonText}
                </Typography>
            )}
        </Box>
    );
};

/* ────────────────────────────────────────
   메시지 버블
   ──────────────────────────────────────── */
const MessageBubble = ({ message, isMine }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        content,
        display_name,
        real_name,           // ★ 관리자/마스터용 실닉
        created_at,
        main_title,
        sub_title,
        equipped_emoticon,
        equipped_emoticon_text,
        is_anonymous_chat,
        is_blinded,
    } = message;

    /* 닉네임 표시 로직 */
    const mergedName =
        real_name && real_name !== display_name
            ? `${display_name} (${real_name})`
            : display_name;

    /* 타임스탬프 포맷 */
    const fullTs = new Date(created_at).toLocaleString('ko-KR', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });

    const timeOnly = new Date(created_at).toLocaleTimeString('ko-KR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });

    const bubbleColor = isMine ? '#dcf8c6' : '#f1f0f0';

    // 익명 여부를 플래그 + 닉네임 패턴 둘 다로 판정
    const isAnonymousDisplay =
        is_anonymous_chat || /^익명/.test(display_name || '');

    let shortName = display_name;

    if (isAnonymousDisplay) {
        const num = (display_name || '').replace(/[^0-9]/g, ''); // 숫자만 추출
        shortName = num ? `익${num}` : '익명';
    }

    // 아바타 배경/텍스트 색상 계산
    const avatarBgColor = getColorFromName(shortName || '');
    const avatarTextColor = getContrastTextColor(avatarBgColor);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: isMine ? 'row-reverse' : 'row',
                alignItems: 'flex-start',
                gap: 1,
                mb: isMobile ? 2 : 1.5,
            }}
        >
            {/* ▣ 왼쪽(또는 오른쪽) 동그라미 닉네임 표기 (내 메시지는 숨김) */}
            {!isMine && (
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: avatarBgColor,
                        color: avatarTextColor,
                        fontSize: '0.8rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        flexShrink: 0,
                        textAlign: 'center',
                        px: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {shortName}
                </Box>
            )}

            {/* ▣ 말풍선 본체 */}
            <Box sx={{ maxWidth: isMobile ? '75%' : '70%' }}>
                <Paper
                    elevation={2}
                    sx={{
                        px: isMobile ? 1.5 : 2,
                        py: isMobile ? 0.8 : 1,
                        bgcolor: bubbleColor,
                        borderRadius: 2,
                        textAlign: isMine ? 'right' : 'left',
                        boxShadow: 1,
                    }}
                >
                    {is_blinded ? (
                        <Typography
                            variant="body2"
                            color="text.disabled"
                            sx={{
                                fontStyle: 'italic',
                                textAlign: 'center',
                            }}
                        >
                            블라인드된 메시지입니다.
                        </Typography>
                    ) : (
                        <>
                            {/* 기존 헤더는 말풍선 내부 위쪽으로 유지 */}
                            <MessageHeader
                                isMine={isMine}
                                name={mergedName}
                                mainTitle={main_title}
                                subTitle={sub_title}
                                emoticonUrl={equipped_emoticon}
                                emoticonText={equipped_emoticon_text}
                                isAnonymousChat={is_anonymous_chat}
                            />

                            <Typography
                                variant="body2"
                                sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {content}
                            </Typography>
                        </>
                    )}

                    {!isMobile && (
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                color: 'text.disabled',
                                mt: 0.5,
                                textAlign: isMine ? 'right' : 'left',
                            }}
                        >
                            {fullTs}
                        </Typography>
                    )}
                </Paper>

                {isMobile && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.disabled',
                            fontSize: '0.7rem',
                            mt: 0.3,
                            textAlign: isMine ? 'right' : 'left',
                        }}
                    >
                        {timeOnly}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default MessageBubble;
